import { useContext, useEffect } from 'react'
import type { MapElement } from '../utils'
import { extendMapBounds, setMapBounds } from '../utils'
import usePrevious from 'hooks/usePrevious'
import type { TripTab } from 'tripPlanner/utils/tabs'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import ExploreQueryContext from 'tripPlanner/contexts/Explore/ExploreQueryContext'
import { DEFAULT_MAP_GEO, DEFAULT_MAP_ZOOM } from 'tripPlanner/config/map'
import { EmptyArray } from 'lib/array/arrayUtils'
import type { ExploreItem } from 'tripPlanner/types/common'
import type { TripItem } from 'tripPlanner/types/tripItem'
import type { PublicTripItem } from 'tripPlanner/types/publicTrip'

export default function useAutoMapBounds(
  mapRef: React.RefObject<google.maps.Map | undefined>,
  isMapLoaded: boolean,
  tab: TripTab,
  isTripLoading: boolean,
  exploreIsLoading: boolean,
  exploreItems: Array<ExploreItem>,
  filteredMapElements: Array<MapElement>,
  allMapElements: Array<MapElement>,
  newOrMovedTripItems: Array<TripItem | PublicTripItem>,
  userAdjustedViewport?: boolean,
) {
  const dispatch = useAppDispatch()
  const prevTab = usePrevious(tab, tab)
  const prevIsTripLoading = usePrevious(isTripLoading, isTripLoading)
  const prevExploreItems = usePrevious(exploreItems, exploreItems)
  const prevFilteredMapElements = usePrevious(filteredMapElements, filteredMapElements)

  const currentUserId = useAppSelector(getCurrentUserId)!
  const { place: selectedPlace } = useContext(ExploreQueryContext)

  useEffect(() => {
    if (!mapRef.current) {
      return
    }

    const setDefaultMapView = () => {
      mapRef.current!.setCenter(DEFAULT_MAP_GEO)
      mapRef.current!.setZoom(DEFAULT_MAP_ZOOM)
    }

    const itemsWithPositionChanges = newOrMovedTripItems.filter(item => {
      const prevItem = prevFilteredMapElements.find(prev => prev.item.id === item.id)
      if (!prevItem) return true

      const currentItem = filteredMapElements.find(curr => curr.item.id === item.id)
      if (!currentItem) return true

      const prevPos = prevItem.position
      const currPos = currentItem.position
      return prevPos.lat !== currPos.lat || prevPos.lng !== currPos.lng
    })

    const selectableItemIds = new Set(itemsWithPositionChanges.map(item => item.id))
    const mapElementsToReveal = filteredMapElements.filter(element => selectableItemIds.has(element.item.id))

    // Only proceed with map updates if there are actual position changes
    const hasPositionChanges = itemsWithPositionChanges.length > 0

    switch (tab) {
      case 'Documents':
      case 'Itinerary':
      case 'Summary':
        // Don't attempt to set bounds while the trip is still loading
        if (isTripLoading) {
          return
        }

        if (filteredMapElements.length === 0 && allMapElements.length === 0 && !selectedPlace) {
          setDefaultMapView()
          return
        }

        // If we've just loaded the trip we need to set the bounds.
        // If we've switched tabs, we want to reset the bounds
        const currentBounds = mapRef.current.getBounds()
        if (prevIsTripLoading || prevTab !== tab || !currentBounds) {
          setMapBounds(
            filteredMapElements.length > 0 ? filteredMapElements : allMapElements,
            mapRef.current,
            selectedPlace,
            !currentBounds,
          )
        } else if (!userAdjustedViewport && filteredMapElements.length > 0 && filteredMapElements !== prevFilteredMapElements && hasPositionChanges) {
          extendMapBounds(filteredMapElements, mapRef.current)
        }

        break
      case 'Explore':
        // Don't update markers if explore is loading... its disorientating
        if (exploreIsLoading) {
          return
        }

        if (!selectedPlace) {
          setDefaultMapView()
          return
        }

        if (prevTab !== tab || exploreItems !== prevExploreItems || !mapRef.current.getBounds()) {
          setMapBounds(
            filteredMapElements.length > 0 ? filteredMapElements : allMapElements,
            mapRef.current,
            selectedPlace,
            prevTab !== tab,
          )
        } else if (
          // Case where we switch TO a place with no items...
          // FROM a place with no items. (Also triggered on load of place with no items)
          // we still need to also update the place in this case.
          exploreItems === prevExploreItems &&
          exploreItems.length === 0
        ) {
          setMapBounds(EmptyArray, mapRef.current, selectedPlace)
        }
    }

    if (!userAdjustedViewport && mapElementsToReveal.length > 0 && hasPositionChanges) {
      extendMapBounds(mapElementsToReveal, mapRef.current)
    }
  }, [currentUserId, filteredMapElements, tab, prevTab, exploreIsLoading, prevExploreItems, exploreItems, selectedPlace, isMapLoaded, mapRef, newOrMovedTripItems, isTripLoading, prevIsTripLoading, prevFilteredMapElements, dispatch, allMapElements, userAdjustedViewport])
}
